import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Divider,
  Card,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Local Import
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxAvatarSide from 'module/leave/pages/adminLeaveSetting/components/AdxAvatarSide'
import AdxLeaveDeleteModal from '../components/AdxLeaveDeleteModal'

// API service
import { useShowLeavePolicy, useDeleteLeavePolicy } from 'services/api/modules/leave/leavePolicy'
import AdxButton from 'components/custom/AdxButton'

const ManageLeavePolicy = () => {
  let navigate = useNavigate()
  let params = useParams()
  let theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('sm'))

  const { data: policy, isLoading } = useShowLeavePolicy(params.id)

  const approvalRoute = policy?.approval_route[0] || {}
  const { name } = approvalRoute
  const routes = [
    approvalRoute.layer_one,
    approvalRoute.layer_two,
    approvalRoute.layer_three,
    approvalRoute.layer_four,
    approvalRoute.layer_five,
  ].filter(Boolean)

  const mutation = useDeleteLeavePolicy()

  const [drawerState, setDrawerState] = useState({ right: true })
  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    navigate(-1)
  }

  const deletePolicy = () => {
    mutation.mutate(policy.id, {
      onSuccess: () => {
        navigate(-1)
      },
    })
  }

  const Header = () => {
    return (
      <Box
        width={1}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Typography variant="h4">Leave Policy</Typography>
        <Box display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
          <AdxLeaveDeleteModal state={policy} deleteFn={deletePolicy} />
          <AdxButton
            variant={'contained'}
            onClick={() => navigate(`../edit/${policy.id}`)}
            sx={{ width: { xs: 100, lg: 100 } }}
          >
            Edit
          </AdxButton>
        </Box>
      </Box>
    )
  }

  const fillerStyle = () => {
    return {
      width: '2px',
      height: '100%',
      backgroundColor: 'gray-line.main',
      borderRadius: '3px',
    }
  }

  if (isLoading) {
    return (
      <AdxDrawerComponent
        state={drawerState}
        anchor="right"
        onClose={handleDrawer}
        sx={{ padding: 0 }}
      >
        <Box
          zIndex={20}
          sx={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="alternate" size={60} />
        </Box>
      </AdxDrawerComponent>
    )
  }

  return (
    <AdxDrawerComponent
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      headerprops={<Header />}
    >
      <Box
        display={'flex'}
        flexDirection={'column'}
        sx={{
          rowGap: 2,
          minHeight: 'calc(100vh - 109px)',
        }}
      >
        {/* Leave Name and Description */}
        <Box display={'flex'} flexDirection="column">
          <Typography color="dark-gray-text.main" variant="subtitle1">
            {policy.name}
          </Typography>

          <Typography color="light-gray-text.main" variant="subtitle1">
            {policy.description}
          </Typography>
        </Box>
        {/* Documentation Required */}
        <Box display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Documentation:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {policy.attachment_required === 1 ? 'Required' : 'Not Required'}
            </Typography>
          </Box>
        </Box>
        {/* Description Reasoning Required */}
        <Box display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Reasoning / Description:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {policy.reason_required === 1 ? 'Required' : 'Not Required'}
            </Typography>
          </Box>
        </Box>
        {/* Replacement Credit Deduction */}
        {policy?.type === 1 && (
          <Box display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 2 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Replacement Credit Deduction:
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 2 },
              }}
            >
              <Typography color="dark-gray-text.main" variant="subtitle1">
                {policy.credit_deduction === 1 ? 'Allowed' : 'Not Allowed'}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Half Day Option */}
        <Box display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Half Day Option:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {policy.half_day_option === 1 ? 'Allowed' : 'Not Allowed'}
            </Typography>
          </Box>
        </Box>
        {/* Incude Non-Working Day Option */}
        <Box px={2} display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Incude Non-Working Day Option:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {policy.include_non_working_days === 1 ? 'Allowed' : 'Not Allowed'}
            </Typography>
          </Box>
        </Box>
        {/* Cycle Period Title*/}
        {policy.type === 1 && (
          <>
            <Box
              my={1.5}
              sx={{
                backgroundColor: 'gray-background.main',
                width: '100%',
                py: 1,
                px: 1.75,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1" color="light-gray-text.main">
                Cycle Period
              </Typography>
            </Box>
            {/* Cycle Period Settings*/}
            <Box display={'flex'} flexDirection={'row'}>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 4 },
                }}
              >
                <Typography color="light-gray-text.main" variant="subtitle1">
                  Leave Cycle Period:
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography
                  color="dark-gray-text.main"
                  variant="subtitle1"
                  textTransform="capitalize"
                >
                  {policy.cycle_period}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Entitlement Title*/}
        <Box
          my={1.5}
          sx={{
            backgroundColor: 'gray-background.main',
            width: '100%',
            py: 1,
            px: 1.75,
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="light-gray-text.main">
            Entitlement
          </Typography>
        </Box>
        {/* Entitlement Settings*/}
        <Box display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 4 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Entitlement Starting:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {policy?.eligible_amount ? policy?.eligible_amount : 'Immediately after hiring'}{' '}
              {policy?.eligible_amount && policy?.eligible_period}
              {policy?.eligible_amount && policy?.eligible_amount > 1 && 's'}{' '}
              {policy?.eligible_amount && 'after hiring'}
            </Typography>
          </Box>
        </Box>
        {/* Entitlement Layers */}
        {policy.type === 1 && (
          <Box my={1}>
            <Card variant="outlined">
              {policy?.entitlement?.map((item, index, arr) => {
                return (
                  <>
                    <Box p={2} display={{ xs: 'column', sm: 'flex' }} alignItems={'center'}>
                      <Typography
                        width={{ xs: 1, sm: 1 / 5 }}
                        variant="body1"
                        color="light-gray-text.main"
                      >
                        Layer {item.layer}
                      </Typography>
                      <Typography
                        width={{ xs: 1, sm: 1 / 2 }}
                        variant="subtitle1"
                        color="dark-gray-text.main"
                      >
                        {item.start_year_of_service} - {item.end_year_of_service} years of service
                      </Typography>
                      <Typography
                        width={{ xs: 1, sm: 1 / 2 }}
                        variant="subtitle1"
                        color="dark-gray-text.main"
                      >
                        {item.amount} days per cycle period
                      </Typography>
                    </Box>

                    {index < arr.length - 1 && <Divider light />}
                  </>
                )
              })}
            </Card>
          </Box>
        )}

        {/* Entitlement Filter*/}
        <Box display={'flex'} flexDirection={'row'}>
          <Typography color="light-gray-text.main" variant="subtitle1">
            This entitlement is available to
          </Typography>
        </Box>
        {/* Entitlement Filter Settings */}
        <Box>
          <Card variant="outlined">
            <Box p={2} display={{ xs: 'column', sm: 'flex' }} alignItems={'center'}>
              <Typography width={{ xs: 1, sm: 1 / 4 }} variant="body1" color="light-gray-text.main">
                Gender
              </Typography>
              <Typography
                width={{ xs: 1, sm: 1 / 2 }}
                variant="subtitle1"
                color="dark-gray-text.main"
              >
                {policy?.category?.gender
                  ?.filter((cat) => cat.status === 1)
                  ?.map((cat, index, arr) =>
                    index < arr.length - 1 ? `${cat.data_name}, ` : cat.data_name
                  )}
              </Typography>
            </Box>

            <Divider light />
            <Box p={2} display={{ xs: 'column', sm: 'flex' }} alignItems={'center'}>
              <Typography width={{ xs: 1, sm: 1 / 4 }} variant="body1" color="light-gray-text.main">
                Marital Status
              </Typography>
              <Typography
                width={{ xs: 1, sm: 1 / 2 }}
                variant="subtitle1"
                color="dark-gray-text.main"
              >
                {policy?.category?.marriage_status
                  ?.filter((cat) => cat.status === 1 && cat.data !== 3)
                  ?.map((cat, index, arr) => {
                    if (cat.data === 2) {
                      if (index < arr.length - 1) {
                        return `Married, `
                      } else {
                        return `Married`
                      }
                    }
                    if (index < arr.length - 1) {
                      return `${cat.data_name}, `
                    } else {
                      return cat.data_name
                    }
                  })}
              </Typography>
            </Box>

            <Divider light />
            <Box p={2} display={{ xs: 'column', sm: 'flex' }} alignItems={'center'}>
              <Typography width={{ xs: 1, sm: 1 / 4 }} variant="body1" color="light-gray-text.main">
                Employment Type
              </Typography>
              <Typography
                width={{ xs: 1, sm: 1 / 2 }}
                variant="subtitle1"
                color="dark-gray-text.main"
              >
                {policy?.category?.employment_type
                  ?.filter((cat) => cat.status === 1)
                  ?.map((cat, index, arr) =>
                    index < arr.length - 1 ? `${cat.data_name}, ` : cat.data_name
                  )}
              </Typography>
            </Box>
          </Card>
        </Box>
        {policy.type === 1 && (
          <>
            {/* Accrual Title */}
            <Box
              my={1.5}
              sx={{
                backgroundColor: 'gray-background.main',
                width: '100%',
                py: 1,
                px: 1.75,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1" color="light-gray-text.main">
                Accrual
              </Typography>
            </Box>
            {/* Accrual option settings 1*/}
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 3.8 },
                }}
              >
                <Typography color="light-gray-text.main" variant="subtitle1">
                  Accrual Option:
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography color="dark-gray-text.main" variant="subtitle1">
                  {policy.accrual_type_name}
                </Typography>
              </Box>
            </Box>
            {/* Accrual Layer settings */}
            {policy.accrual_type !== 1 ? (
              <>
                {policy?.entitlement.map((item) => {
                  let prorated_amount = (item.amount / 12).toFixed(2)
                  return (
                    <Box display={{ xs: 'column', sm: 'flex' }} flexDirection={'row'}>
                      <Box
                        sx={{
                          width: { xs: 1, sm: 1 / 3.8 },
                        }}
                      ></Box>
                      <Box
                        sx={{
                          width: { xs: 1, sm: 1 / 2 },
                        }}
                      >
                        <Typography color="dark-gray-text.main" variant="subtitle1">
                          Layer {item.layer} - {prorated_amount} days accrued per month
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </>
            ) : null}
            {/* Accrual option settings 2*/}
            {policy.accrual_type !== 1 ? (
              <>
                <Box display={{ xs: 'column', sm: 'flex' }} flexDirection={'row'}>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 3.8 },
                    }}
                  >
                    <Typography color="light-gray-text.main" variant="subtitle1">
                      Accrual Happens At:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 2 },
                    }}
                  >
                    <Typography color="dark-gray-text.main" variant="subtitle1">
                      {policy.accrual_happen === 'start_month' ? 'Start' : 'End'} of the month
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : null}
          </>
        )}

        {/* Approval Title */}
        <Box
          my={1.5}
          sx={{
            backgroundColor: 'gray-background.main',
            width: '100%',
            py: 1,
            px: 1.75,
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="light-gray-text.main">
            Approval
          </Typography>
        </Box>
        {/* Approval Settings */}
        <Box display={{ xs: 'column', sm: 'flex', lg: 'flex' }} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 3.8 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Approval Flow:
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 2 },
            }}
          >
            <Typography color="dark-gray-text.main" variant="subtitle1">
              {name}
            </Typography>
          </Box>
        </Box>
        {/* Approval Label Card */}
        <Box display={'flex'} flexDirection={'row'}>
          <Box
            sx={{
              width: { xs: 1, sm: 1 / 4 },
            }}
          >
            <Typography color="light-gray-text.main" variant="subtitle1">
              Flow:
            </Typography>
          </Box>
        </Box>
        {/* Approval Card */}
        <Box>
          <Card variant="outlined">
            {routes.map((route, index, arr) => {
              return (
                <React.Fragment key={index}>
                  <Box p={2} display={'flex'} alignItems={'center'} columnGap={2}>
                    <Typography
                      minWidth={82}
                      flexShrink={0}
                      variant="body1"
                      color="light-gray-text.main"
                    >
                      Step {index + 1}
                    </Typography>
                    {matches ? (
                      <Box width={110} flexShrink={0}>
                        <AdxAvatarSide tooltipMax={4} active data={route.members} />
                      </Box>
                    ) : null}
                    <Box display={'flex'} flexDirection={'column'} minWidth={0}>
                      <Typography noWrap variant="body1" color="dark-gray-text.main">
                        {route.name}
                      </Typography>
                      <Typography noWrap variant="subtitle2" color="light-gray-text.main">
                        {route?.members?.map((member, index, arr) => {
                          if (index === arr.length - 1) {
                            return `${member.name}`
                          }
                          return `${member.name}, `
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {index < arr.length - 1 && <Divider variant="full" />}
                </React.Fragment>
              )
            })}
          </Card>
        </Box>
        {/* Advanced Settings Title */}
        <Box
          my={1.5}
          sx={{
            backgroundColor: 'gray-background.main',
            width: '100%',
            py: 1,
            px: 1.75,
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="light-gray-text.main">
            Advanced Settings
          </Typography>
        </Box>
        <Box mb={2.5} display="flex" flexDirection="column" gap={2.5}>
          {/* Application Quota */}
          {policy.type === 1 && (
            <>
              {policy.quota_amount === null ? (
                <Box display={'flex'} flexDirection={'row'}>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 2 },
                    }}
                  >
                    <Typography color="light-gray-text.main" variant="subtitle1">
                      Leave Application Quota:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 2 },
                    }}
                  >
                    <Typography color="dark-gray-text.main" variant="subtitle1">
                      Disabled
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box>
                    <AdxFormControlLabel
                      type="no-fill"
                      control={<AdxCheckbox checked={true} />}
                      label="Enable Leave Application Quota"
                    />
                  </Box>
                  <Box
                    pl={7}
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    sx={{ width: 1 }}
                  >
                    {matches ? (
                      <Grid sx={{ width: 1 / 20 }}>
                        <Box sx={{ ...fillerStyle() }}></Box>{' '}
                      </Grid>
                    ) : null}
                    <Box width={{ xs: 1, sm: 1 / 5 }}>
                      <Typography variant="subtitle1" color="light-gray-text.main">
                        Daily Quota
                      </Typography>
                    </Box>
                    <Box width={{ xs: 1, sm: 1 / 3 }}>
                      <Typography variant="subtitle1" color="dark-gray-text.main">
                        {`${policy.quota_amount} ${
                          policy.quota_unit === 'number' ? 'people' : policy.quota_unit
                        } per ${policy.quota_category}`}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}

          {/* Leave Restriction */}
          {!policy.restriction_amount ? (
            <Box display={'flex'} flexDirection={'row'}>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography color="light-gray-text.main" variant="subtitle1">
                  Leave Application Restriction:
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography color="dark-gray-text.main" variant="subtitle1">
                  Disabled
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box>
                <AdxFormControlLabel
                  type="no-fill"
                  control={<AdxCheckbox checked={true} />}
                  label="Enable Leave Application Restriction"
                />
              </Box>
              {/* Leave Restriction Description */}
              <Box
                pl={7}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                sx={{ width: 1 }}
              >
                {matches ? (
                  <Grid sx={{ width: 1 / 20 }}>
                    <Box sx={{ ...fillerStyle() }}></Box>{' '}
                  </Grid>
                ) : null}
                <Box width={{ xs: 1, sm: 1 / 5 }}>
                  <Typography variant="subtitle1" color="light-gray-text.main">
                    Restriction
                  </Typography>
                </Box>
                <Box width={{ xs: 1, sm: 1 / 3 }}>
                  <Typography variant="subtitle1" color="dark-gray-text.main">
                    {policy.restriction_amount} days in a row (maximum)
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Carry Forward */}
          {policy.type === 1 && (
            <>
              {policy.carry_forward_amount === null ? (
                <Box display={'flex'} flexDirection={'row'}>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 2 },
                    }}
                  >
                    <Typography color="light-gray-text.main" variant="subtitle1">
                      Carry Forward:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 1, sm: 1 / 2 },
                    }}
                  >
                    <Typography color="dark-gray-text.main" variant="subtitle1">
                      Disabled
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box>
                    <AdxFormControlLabel
                      type="no-fill"
                      control={<AdxCheckbox checked={true} />}
                      label="Allow Carry Forward"
                    />
                  </Box>
                  {/* Carry Forward Description 1*/}
                  <Box
                    pl={7}
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    sx={{ width: 1 }}
                  >
                    {matches ? (
                      <Grid sx={{ width: 1 / 20 }}>
                        <Box sx={{ ...fillerStyle() }}></Box>{' '}
                      </Grid>
                    ) : null}
                    <Box width={{ xs: 1, sm: 1 / 3 }}>
                      <Typography variant="subtitle1" color="light-gray-text.main">
                        Number of days allowed
                      </Typography>
                    </Box>
                    <Box width={{ xs: 1, sm: 1 / 3 }}>
                      <Typography variant="subtitle1" color="dark-gray-text.main">
                        {policy.carry_forward_amount === -1
                          ? 'Unlimited'
                          : `Up to ${policy.carry_forward_amount} days`}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Carry Forward Description 2*/}
                  <Box
                    pl={7}
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    sx={{ width: 1 }}
                  >
                    {matches ? (
                      <Grid sx={{ width: 1 / 20 }}>
                        <Box sx={{ ...fillerStyle() }}></Box>{' '}
                      </Grid>
                    ) : null}
                    <Box width={{ xs: 1, sm: 1 / 3 }}>
                      <Typography variant="subtitle1" color="light-gray-text.main">
                        Carry Forward Expiry
                      </Typography>
                    </Box>
                    <Box width={{ xs: 1, sm: 1 / 3 }}>
                      {policy.carry_forward_expiry_period === 'no_expiry' ||
                      policy.carry_forward_expiry_period === 'cycle_period' ? (
                        <Typography variant="subtitle1" color="dark-gray-text.main">
                          {policy.carry_forward_expiry_period_name}
                        </Typography>
                      ) : (
                        <Typography variant="subtitle1" color="dark-gray-text.main">
                          {policy.carry_forward_expiry_amount} {policy.carry_forward_expiry_period}
                          {policy.carry_forward_expiry_amount > 1 ? 's' : null} after new cycle
                          period
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}

          {/* Leave Application Notice */}
          {policy.day_prior === null ? (
            <Box display={'flex'} flexDirection={'row'}>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography color="light-gray-text.main" variant="subtitle1">
                  Leave Application Notice Period:
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 1, sm: 1 / 2 },
                }}
              >
                <Typography color="dark-gray-text.main" variant="subtitle1">
                  Disabled
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box>
                <AdxFormControlLabel
                  type="no-fill"
                  control={<AdxCheckbox checked={true} />}
                  label="Allow Leave Application Notice Period"
                />
              </Box>
              {/* Leave Notice Description 1*/}
              <Box
                pl={7}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                sx={{ width: 1 }}
              >
                {matches ? (
                  <Grid sx={{ width: 1 / 20 }}>
                    <Box sx={{ ...fillerStyle() }}></Box>{' '}
                  </Grid>
                ) : null}
                <Box width={{ xs: 1, sm: 1 / 3 }}>
                  <Typography variant="subtitle1" color="light-gray-text.main">
                    Number of days required:
                  </Typography>
                </Box>
                <Box width={{ xs: 1, sm: 1 / 3 }}>
                  <Typography variant="subtitle1" color="dark-gray-text.main">
                    {policy.day_prior !== 1
                      ? `${policy.day_prior} days `
                      : `${policy.day_prior} day `}
                    before requested date
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </AdxDrawerComponent>
  )
}

export default ManageLeavePolicy
